import { useEffect, useState } from 'react'
import { Link, Navigate, useParams } from 'react-router-dom'
import { getPostType } from '../lib/cms'
import { storyPath } from '../lib/paths'
import { api, type AdminPost } from './api'
import Pager from './Pager'

const PAGE_SIZE = 12

function matchesQuery(post: AdminPost, query: string) {
  if (!query) return true
  const haystack = [post.title, post.slug, post.dek, post.author, post.status].join(' ').toLowerCase()
  return haystack.includes(query)
}

export default function AdminPosts() {
  const { type: typeSlug } = useParams()
  const postType = getPostType(typeSlug)
  const [posts, setPosts] = useState<AdminPost[] | null>(null)
  const [error, setError] = useState('')
  const [page, setPage] = useState(1)
  const [query, setQuery] = useState('')

  useEffect(() => {
    if (!postType) return
    setPosts(null)
    setPage(1)
    setQuery('')
    api<AdminPost[]>('/api/admin/posts')
      .then((all) => setPosts(all.filter((post) => post.category === postType.category)))
      .catch((err: Error) => setError(err.message))
  }, [postType])

  if (!postType) return <Navigate to="/admin" replace />
  if (error) return <p className="text-rose">{error}</p>
  if (!posts) return <p className="text-muted">Loading {postType.menu.toLowerCase()}…</p>

  const needle = query.trim().toLowerCase()
  const filtered = posts.filter((post) => matchesQuery(post, needle))
  const pages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE))
  const current = Math.min(page, pages)
  const visible = filtered.slice((current - 1) * PAGE_SIZE, current * PAGE_SIZE)

  return (
    <section>
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="text-caps text-[10px] text-rose">Post type</p>
          <h2 className="mt-1 font-serif text-[32px] leading-none font-medium">{postType.menu}</h2>
        </div>
        <Link
          to={`/admin/${postType.slug}/new`}
          className="text-caps inline-flex h-11 items-center bg-ink px-5 text-white hover:bg-rose"
        >
          {postType.addLabel} {postType.singular}
        </Link>
      </div>
      {posts.length === 0 ? (
        <p className="mt-10 max-w-[46ch] text-copy">
          No {postType.menu.toLowerCase()} in the studio yet. Add one and it stays in this type only.
        </p>
      ) : (
        <>
          <label className="mt-8 block max-w-xl">
            <span className="sr-only">Search {postType.menu.toLowerCase()}</span>
            <input
              type="search"
              value={query}
              onChange={(event) => {
                setQuery(event.target.value)
                setPage(1)
              }}
              placeholder={`Search ${postType.menu.toLowerCase()}…`}
              className="w-full border border-rule bg-paper px-3 py-2.5 text-[15px] outline-none focus:border-ink"
            />
          </label>
          {filtered.length === 0 ? (
            <p className="mt-8 text-sm text-muted">No {postType.menu.toLowerCase()} match that search.</p>
          ) : (
            <>
              <ul className="mt-5 divide-y divide-rule border-y border-rule">
                {visible.map((post) => (
                  <li key={post.id} className="flex items-start justify-between gap-4 py-2">
                    <div className="min-w-0">
                      <Link
                        to={`/admin/${postType.slug}/${post.id}`}
                        className="block truncate text-[15px] leading-snug hover:text-rose"
                      >
                        {post.title}
                      </Link>
                      <div className="mt-0.5 flex gap-3 text-[11px] leading-none tracking-[0.12em] uppercase">
                        <Link to={`/admin/${postType.slug}/${post.id}`} className="text-muted hover:text-rose">
                          Edit
                        </Link>
                        {post.status === 'published' ? (
                          <a href={storyPath(post.slug)} target="_blank" rel="noreferrer" className="text-muted hover:text-rose">
                            View post
                          </a>
                        ) : null}
                      </div>
                    </div>
                    <span
                      className={`shrink-0 pt-0.5 text-[11px] tracking-[0.16em] uppercase ${
                        post.status === 'published' ? 'text-rose' : post.status === 'scheduled' ? 'text-ink' : 'text-stone'
                      }`}
                    >
                      {post.status === 'scheduled' && post.publishAt
                        ? `Goes live ${new Date(post.publishAt).toLocaleString([], { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}`
                        : post.status}
                    </span>
                  </li>
                ))}
              </ul>
              <Pager page={current} pages={pages} onPage={setPage} />
            </>
          )}
        </>
      )}
    </section>
  )
}
